import {
  IAutoCheckout,
  IAutoCheckoutBilling,
  IAutoQuote,
  ILineItem,
  IVehicle,
  TPolicy,
} from '../../interfaces';
import { formatPrice } from '../../services';

type TAutoStateFeeItemCode = 'state_theft_fee' | 'state_fraud_assessment_fee';
type TStateFeeAmount = ILineItem | null | undefined;

function getQuoteApplicableStateFee(
  monthlyBillingAmounts: IAutoCheckoutBilling,
  itemCode: TAutoStateFeeItemCode
) {
  return monthlyBillingAmounts
    ? monthlyBillingAmounts.line_items.find(
        ({ item_code }) => item_code === itemCode
      )
    : null;
}

function getFeePerVehicle(feeAmount: TStateFeeAmount, numVehicles: number) {
  return feeAmount ? Number(feeAmount?.amount) / (numVehicles || 1) : undefined;
}

export function getQuoteStateFees(
  quote: IAutoQuote,
  quoteCheckout: { billing_amounts: Array<IAutoCheckoutBilling> },
  stateTheftFeeEligibilityCheck?: (v: IVehicle) => boolean
) {
  const monthlyBillingAmounts = quoteCheckout?.billing_amounts.find(
    ({ billing_cadence }) => billing_cadence === 'monthly'
  );

  const stateTheftFee = getQuoteApplicableStateFee(
    monthlyBillingAmounts as IAutoCheckoutBilling,
    'state_theft_fee'
  );

  const stateFraudAssessmentFee = getQuoteApplicableStateFee(
    monthlyBillingAmounts as IAutoCheckoutBilling,
    'state_fraud_assessment_fee'
  );

  const numVehiclesOnQuote = quote?.application.vehicles?.length || 0;
  const theftFeeEligibleVehiclesOnQuote =
    quote?.application.vehicles?.filter((vehicle) =>
      stateTheftFeeEligibilityCheck
        ? stateTheftFeeEligibilityCheck(vehicle)
        : true
    ).length || 0;

  const stateTheftFeePerVehicle = getFeePerVehicle(
    stateTheftFee,
    theftFeeEligibleVehiclesOnQuote
  );

  const stateFraudAssessmentFeePerVehicle = getFeePerVehicle(
    stateFraudAssessmentFee,
    numVehiclesOnQuote
  );

  return {
    stateTheftFee,
    stateFraudAssessmentFee,
    stateTheftFeePerVehicle,
    stateFraudAssessmentFeePerVehicle,
  };
}

export function getEditPolicyStateFees(
  policy: TPolicy,
  policyCheckout: IAutoCheckout
) {
  function getPolicyStateFee(itemCode: string) {
    return (
      policyCheckout?.billing_amounts
        .find(
          (billingAmount) =>
            billingAmount.billing_cadence === policy?.billing_cadence
        )
        ?.line_items?.find((lineItem) => lineItem.item_code === `${itemCode}`)
        ?.amount || 0
    );
  }
  const policyStateFraudAssessmentFee = getPolicyStateFee(
    'state_fraud_assessment_fee'
  );

  const policyFormattedStateFraudAssessmentFee = formatPrice(
    Number(policyStateFraudAssessmentFee)
  );

  const policyStateTheftFee = getPolicyStateFee('state_theft_fee');

  return {
    policyStateTheftFee,
    policyStateFraudAssessmentFee,
    policyFormattedStateFraudAssessmentFee,
  };
}
